import { inject } from '@angular/core';
import { CanActivateChildFn, CanActivateFn, Router, UrlTree } from '@angular/router';
import { CasdoorAuthService } from 'app/core/auth/casdoor-auth.service';
import { AuthAlertService } from 'app/core/auth/auth-alert.service';
import { AuthInitializationService } from 'app/core/auth/auth-initialization.service';
import { NoRoleAssignedError, UserProfileService } from 'app/modules/dashboard/dipita/user-profile/user-profile.service';
import { getDefaultRedirectPath } from 'app/modules/dashboard/dipita/user-profile/user-profile.types';
import { Observable, catchError, from, of, switchMap } from 'rxjs';

/**
 * Routes the user to the right landing page for their role, and stops accounts that have no
 * role yet from entering the app.
 *
 * An incomplete profile is deliberately NOT blocking: the profile is filled from Settings
 * whenever the user wants, and a reminder banner nudges them. Only a missing role is
 * blocking, because without one the backend rejects every business call.
 */
export const DashboardEntryGuard: CanActivateFn | CanActivateChildFn = (route, state): Observable<boolean | UrlTree> => {
    const router = inject(Router);
    const authService = inject(CasdoorAuthService);
    const authAlertService = inject(AuthAlertService);
    const authInitService = inject(AuthInitializationService);
    const userProfileService = inject(UserProfileService);

    const isDashboardRoot = (url: string): boolean => url === '/dashboard' || url === '/dashboard/';

    const rejectPendingAccount = (): Observable<UrlTree> => {
        authAlertService.showAccountPendingAlert();
        authInitService.reset();
        authInitService.logoutWithReturnUrl();
        return of(router.parseUrl('/sign-in'));
    };

    const isNoRoleAssigned = (error: any): boolean =>
        error instanceof NoRoleAssignedError || error?.name === 'NoRoleAssignedError';

    return authService.isAuthenticated$.pipe(
        switchMap((authenticated) => {
            if (!authenticated) {
                return of(true);
            }

            // Cheap on every route (the status call is cached): an account whose role hasn't
            // been granted yet gets 403 on every business call, so it must not enter the app.
            return from(userProfileService.getProfileStatus()).pipe(
                switchMap(() => {
                    // Deeper routes are already explicit - only /dashboard itself needs
                    // resolving to the landing page matching the user's role.
                    if (!isDashboardRoot(state.url)) {
                        return of(true);
                    }

                    return from(userProfileService.getProfile()).pipe(
                        switchMap((clinicianProfile) => {
                            const redirectPath = clinicianProfile
                                ? getDefaultRedirectPath(clinicianProfile.roles)
                                : '/dashboard/requester/request';
                            return of(router.parseUrl(redirectPath));
                        }),
                        catchError((error) => {
                            if (isNoRoleAssigned(error)) {
                                return rejectPendingAccount();
                            }
                            // A profile that doesn't exist yet is a normal state now - send the
                            // user in and let the reminder banner take over.
                            return of(router.parseUrl('/dashboard/requester/request'));
                        })
                    );
                }),
                catchError((statusError) => {
                    if (isNoRoleAssigned(statusError)) {
                        return rejectPendingAccount();
                    }
                    // Don't lock the user out over a transient status failure.
                    return of(isDashboardRoot(state.url)
                        ? router.parseUrl('/dashboard/requester/request')
                        : true);
                })
            );
        })
    );
};
